import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { FiCalendar, FiChevronDown, FiClock, FiCheck, FiX, FiEdit2 } from "react-icons/fi";
import { title, pageTitle } from "../utils/style";
import fetchData from "../custom_functions/fetchData";
import { url } from "../utils/config";
import QuotationRequestPanel from "../child_components/QuotationRequestPanel";
import myInfoAtom from "../atoms/myInfoAtom";
import postData from "../custom_functions/postData";
import patchData from "../custom_functions/patchData";
import globalConfigAtom from "../atoms/globalConfigAtom";
import { formatDate } from "../helperFunctions/estimateCalculation";

const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
});

function formatCurrency(value) {
    const numeric = Number(value);
    return currencyFormatter.format(Number.isFinite(numeric) ? numeric : 0);
}

function formatDateTime(value) {
    if (!value) return "-";
    try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;
        return new Intl.DateTimeFormat("en-US", {
            dateStyle: "medium",
            timeStyle: "short",
        }).format(date);
    } catch (error) {
        return value;
    }
}

function formatStatus(status) {
    if (!status) return "-";
    return String(status)
        .replace(/[_-]/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getStatusBadgeClass(status) {
    const normalized = String(status || "").toLowerCase();
    const map = {
        pending: "bg-amber-100 text-amber-700 border border-amber-200",
        approved: "bg-emerald-100 text-emerald-700 border border-emerald-200",
        inservice: "bg-cyan-100 text-cyan-700 border border-cyan-200",
        completed: "bg-green-100 text-green-700 border border-green-200",
        rejected: "bg-rose-100 text-rose-700 border border-rose-200",
        modified: "bg-blue-100 text-blue-700 border border-blue-200",
        failed: "bg-red-100 text-red-700 border border-red-200",
        paid: "bg-purple-100 text-purple-700 border border-purple-200",
    };
    return map[normalized] || "bg-slate-100 text-slate-600 border border-slate-200";
}

function getStatusIcon(status) {
    const normalized = String(status || "").toLowerCase();
    switch (normalized) {
        case "approved":
        case "inservice":
        case "completed":
        case "paid":
            return <FiCheck size={12} />;
        case "rejected":
        case "failed":
            return <FiX size={12} />;
        case "modified":
            return <FiEdit2 size={12} />;
        case "pending":
        default:
            return <FiClock size={12} />;
    }
}

function getStatusColor(status) {
    const normalized = String(status || "").toLowerCase();
    const colors = {
        pending: {
            bg: "bg-amber-500",
            border: "border-amber-500",
            text: "text-amber-700",
            lightBg: "bg-amber-50",
            line: "bg-amber-300",
        },
        approved: {
            bg: "bg-emerald-500",
            border: "border-emerald-500",
            text: "text-emerald-700",
            lightBg: "bg-emerald-50",
            line: "bg-emerald-300",
        },
        inservice: {
            bg: "bg-cyan-500",
            border: "border-cyan-500",
            text: "text-cyan-700",
            lightBg: "bg-cyan-50",
            line: "bg-cyan-300",
        },
        completed: {
            bg: "bg-green-600",
            border: "border-green-600",
            text: "text-green-700",
            lightBg: "bg-green-50",
            line: "bg-green-300",
        },
        rejected: {
            bg: "bg-rose-500",
            border: "border-rose-500",
            text: "text-rose-700",
            lightBg: "bg-rose-50",
            line: "bg-rose-300",
        },
        modified: {
            bg: "bg-blue-500",
            border: "border-blue-500",
            text: "text-blue-700",
            lightBg: "bg-blue-50",
            line: "bg-blue-300",
        },
        failed: {
            bg: "bg-red-500",
            border: "border-red-500",
            text: "text-red-700",
            lightBg: "bg-red-50",
            line: "bg-red-300",
        },
        paid: {
            bg: "bg-purple-500",
            border: "border-purple-500",
            text: "text-purple-700",
            lightBg: "bg-purple-50",
            line: "bg-purple-300",
        },
    };
    return colors[normalized] || {
        bg: "bg-slate-400",
        border: "border-slate-400",
        text: "text-slate-600",
        lightBg: "bg-slate-50",
        line: "bg-slate-300",
    };
}

function formatTrackingDate(value) {
    if (!value) return "-";
    try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;
        return new Intl.DateTimeFormat("en-US", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        }).format(date);
    } catch (error) {
        return value;
    }
}

function StatusTrackingFlow({ statusTracking }) {
    if (!Array.isArray(statusTracking) || statusTracking.length === 0) {
        return null;
    }

    const sortedTracking = [...statusTracking].sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA - dateB;
    });

    return (
        <div className="mt-4">
            <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-emerald-500">
                Status Flow
            </div>
            <div className="relative flex items-center justify-between overflow-x-auto pb-2">
                {/* Connecting line */}
                <div className="absolute top-4 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-200 via-emerald-300 to-emerald-200 z-0" />

                {sortedTracking.map((track, index) => {
                    const colors = getStatusColor(track.status);
                    const isLast = index === sortedTracking.length - 1;

                    return (
                        <div
                            key={track.id || index}
                            className="relative z-10 flex flex-col items-center min-w-[80px] flex-1"
                        >
                            {/* Status circle */}
                            <div
                                className={`flex h-8 w-8 items-center justify-center rounded-full ${colors.bg} text-white shadow-md ring-4 ring-white transition-transform hover:scale-110`}
                            >
                                {getStatusIcon(track.status)}
                            </div>

                            {/* Status label */}
                            <div
                                className={`mt-2 rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${colors.lightBg} ${colors.text}`}
                            >
                                {formatStatus(track.status)}
                            </div>

                            {/* Date */}
                            <div className="mt-1 text-[10px] text-slate-500 text-center whitespace-nowrap">
                                {formatTrackingDate(track.date)}
                            </div>

                            {/* Arrow indicator (except for last item) */}
                            {!isLast && (
                                <div className="absolute top-3 right-0 translate-x-1/2 text-emerald-400 hidden sm:block">
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                                        <path d="M4.5 2L8.5 6L4.5 10" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function useDebouncedValue(value, delay) {
    const [debounced, setDebounced] = useState(value);

    useEffect(() => {
        const handle = setTimeout(() => {
            setDebounced(value);
        }, delay);
        return () => clearTimeout(handle);
    }, [value, delay]);

    return debounced;
}

function normalizeVehicle(vehicle) {
    if (!vehicle) return null;

    return {
        id: vehicle.id,
        year: vehicle?.year,
        status: vehicle?.status,
        make: vehicle?.make,
        model: vehicle?.model,
        vin: vehicle?.vin,
        plate: vehicle?.plate,
        raw: vehicle,
    };
}

function normalizeService(bundle, labourRate) {
    if (!bundle) return null;
    const serviceId = bundle?.id ?? bundle?.bundle_id ?? bundle?.code;
    const costType = bundle?.costType || "Fixed";
    const estimatedTime = Number(bundle?.estimatedTime) || 0;
    const estimatedCost = Number(bundle?.estimatedCost ?? bundle?.price ?? bundle?.cost ?? 0) || 0;

    // Calculate price based on costType
    let price = 0;
    if (costType === "Hourly") {
        price = (labourRate / 60) * estimatedTime;
    } else {
        // Fixed cost
        price = estimatedCost;
    }

    return {
        id: serviceId || null,
        title: bundle?.name || bundle?.bundle_name || "Untitled Service",
        description: bundle?.description || bundle?.shortDescription || "",
        price: Number.isFinite(price) ? price : 0,
        costType,
        estimatedTime,
        estimatedCost,
        raw: bundle,
    };
}

function Quotations() {
    const [showQuotationReq, setShowQuotationReq] = useState(false);
    const [activeTab, setActiveTab] = useState("Select Vehicle");
    const [vehicles, setVehicles] = useState([]);
    const [isVehiclesLoading, setIsVehiclesLoading] = useState(false);
    const [vehicleSearch, setVehicleSearch] = useState("");
    const [serviceBundles, setServiceBundles] = useState([]);
    const [partList, setPartList] = useState([]);
    const [serviceBundleList, setServiceBundleList] = useState([]);
    const [isServiceLoading, setIsServiceLoading] = useState(false);
    const [isPartListLoading, setIsPartListLoading] = useState(false);
    const [isServiceBundleListLoading, setIsServiceBundleListLoading] = useState(false);
    const [hasRequestedServices, setHasRequestedServices] = useState(false);
    const [selectedVehicleId, setSelectedVehicleId] = useState(null);
    const [selectedServiceIds, setSelectedServiceIds] = useState([]);
    const [selectedPartIds, setSelectedPartIds] = useState([]);
    const [selectedBundleIds, setSelectedBundleIds] = useState([]);
    const [bundleMetadata, setBundleMetadata] = useState({}); // stores tire type, quantity, notes for each bundle
    const [additionalNotes, setAdditionalNotes] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [reloadTableToggle, setReloadTableToggle] = useState(false);
    const [quotes, setQuotes] = useState([]);
    const [isQuotesLoading, setIsQuotesLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [isNextPage, setIsNextPage] = useState(false);
    const [statusFilter, setStatusFilter] = useState("all");
    const [customServiceList, setCustomServiceList] = useState([]);
    const [tireQuantity, setTireQuantity] = useState("");
    const [tireNotes, setTireNotes] = useState("");
    const [isMaintenance, setIsMaintenance] = useState(false);
    const [expandedQuoteId, setExpandedQuoteId] = useState(null);
    const [approvingQuoteId, setApprovingQuoteId] = useState(null);
    const [rejectingQuoteId, setRejectingQuoteId] = useState(null);
    const [paymentLoadingQuoteId, setPaymentLoadingQuoteId] = useState(null);
    const globalConfig = useRecoilValue(globalConfigAtom);
    const myInfo = useRecoilValue(myInfoAtom);

    const labourRate = Number(globalConfig?.defaultTechnicianCost) || 0;
    const taxRate = Number(globalConfig?.defaultTax) || 0;
    const debouncedVehicleSearch = useDebouncedValue(vehicleSearch, 300);

    const hasQuotes = quotes.length > 0;
    const canGoBack = page > 1;
    const canGoForward = Boolean(isNextPage);


    const normalizedVehicles = useMemo(() => {
        if (!Array.isArray(vehicles)) return [];
        return vehicles
            .map(normalizeVehicle)
            .filter((item) => item && item.id);
    }, [vehicles]);

    useEffect(() => {
        setExpandedQuoteId(null);
    }, [page, statusFilter, reloadTableToggle]);

    const normalizedServices = useMemo(() => {
        if (!Array.isArray(serviceBundles)) return [];
        return serviceBundles
            .map((bundle) => normalizeService(bundle, labourRate))
            .filter((item) => item && item.id);
    }, [serviceBundles, labourRate]);

    const normalizedPartList = useMemo(() => {
        if (!Array.isArray(partList)) return [];
        return partList
            .map((part) => {
                const baseCost = Number(part?.cost ?? part?.price ?? 0) || 0;
                const estimatedTime = Number(part?.estimatedTime) || 0;
                const labourCost = estimatedTime * (labourRate / 60);
                const totalPrice = baseCost + labourCost;
                return {
                    id: part?.id ?? part?.code,
                    title: part?.name || "Untitled Part",
                    price: totalPrice,
                    baseCost,
                    labourCost,
                    estimatedTime,
                    costType: part?.costType || "",
                };
            })
            .filter((item) => item && item.id);
    }, [partList, labourRate]);

    const normalizedServiceBundleList = useMemo(() => {
        if (!Array.isArray(serviceBundleList)) return [];
        return serviceBundleList
            .map((bundle) => ({
                id: bundle?.id ?? bundle?.code,
                title: bundle?.name || "Untitled Bundle",
                price: Number(bundle?.cost ?? bundle?.price ?? 0) || 0,
            }))
            .filter((item) => item && item.id);
    }, [serviceBundleList]);

    const selectedVehicle = useMemo(() => {
        return normalizedVehicles.find((vehicle) => vehicle.id === selectedVehicleId) || null;
    }, [normalizedVehicles, selectedVehicleId]);

    const selectedServices = useMemo(() => {
        if (!Array.isArray(selectedServiceIds) || selectedServiceIds.length === 0) return [];
        return normalizedServices.filter((service) => selectedServiceIds.includes(service.id));
    }, [normalizedServices, selectedServiceIds]);

    const selectedParts = useMemo(() => {
        if (!Array.isArray(selectedPartIds) || selectedPartIds.length === 0) return [];
        return normalizedPartList.filter((part) => selectedPartIds.includes(part.id));
    }, [normalizedPartList, selectedPartIds]);

    const selectedBundles = useMemo(() => {
        if (!Array.isArray(selectedBundleIds) || selectedBundleIds.length === 0) return [];
        return normalizedServiceBundleList.filter((bundle) => selectedBundleIds.includes(bundle.id));
    }, [normalizedServiceBundleList, selectedBundleIds]);

    const totalPrice = useMemo(() => {
        const servicesTotal = selectedServices.reduce((sum, service) => sum + (Number(service.price) || 0), 0);
        const partsTotal = selectedParts.reduce((sum, part) => sum + (Number(part.price) || 0), 0);
        const bundlesTotal = selectedBundles.reduce((sum, bundle) => sum + (Number(bundle.price) || 0), 0);
        return servicesTotal + partsTotal + bundlesTotal;
    }, [selectedServices, selectedParts, selectedBundles]);

    const customServicesTotal = useMemo(() => {
        return customServiceList.reduce((sum, service) => {
            const value = Number(service.price);
            return sum + (Number.isFinite(value) ? value : 0);
        }, 0);
    }, [customServiceList]);

    const partsBaseCostTotal = useMemo(() => {
        return selectedParts.reduce((sum, part) => sum + (Number(part.baseCost) || 0), 0);
    }, [selectedParts]);

    const taxAmount = useMemo(() => {
        return (partsBaseCostTotal * taxRate) / 100;
    }, [partsBaseCostTotal, taxRate]);

    const overallTotal = useMemo(() => totalPrice + customServicesTotal + taxAmount, [totalPrice, customServicesTotal, taxAmount]);

    useEffect(() => {
        if (!showQuotationReq) return;
        setIsVehiclesLoading(true);
        const params = new URLSearchParams();
        params.append("status", "Active");
        if (debouncedVehicleSearch) {
            params.append("search", debouncedVehicleSearch);
        }

        fetchData(
            `${url}/fleet_customer_car?${params.toString()}`,
            (error) => {
                setVehicles([]);
                setSelectedVehicleId(null);
                setIsVehiclesLoading(false);
                toast.error(error?.response?.data?.message || "Failed to load fleet vehicles", {
                    autoClose: 3000,
                });
            },
            (response) => {
                const nextVehicles = Array.isArray(response?.data) ? response.data : [];
                setVehicles(nextVehicles);
                let shouldClearSelection = false;
                setSelectedVehicleId((prev) => {
                    if (!prev) return prev;
                    const hasMatch = nextVehicles.some((item) => {
                        const normalized = normalizeVehicle(item);
                        return normalized?.id === prev;
                    });
                    if (hasMatch) return prev;
                    shouldClearSelection = true;
                    return null;
                });
                if (shouldClearSelection) {
                    setSelectedServiceIds([]);
                    setCustomServiceList([]);
                }
                setIsVehiclesLoading(false);
            },
            false
        );
    }, [showQuotationReq, debouncedVehicleSearch]);

    useEffect(() => {
        if (!showQuotationReq) {
            setHasRequestedServices(false);
            setSelectedVehicleId(null);
            setSelectedServiceIds([]);
            setSelectedPartIds([]);
            setSelectedBundleIds([]);
            setBundleMetadata({});
            setAdditionalNotes("");
            setActiveTab("Select Vehicle");
            setCustomServiceList([]);
            setTireQuantity("");
            setTireNotes("");
            setVehicleSearch("");
            setVehicles([]);
            setServiceBundles([]);
            setPartList([]);
            setServiceBundleList([]);
        }
    }, [showQuotationReq]);

    useEffect(() => {
        const hasServices = Array.isArray(serviceBundles) && serviceBundles.length > 0;
        const shouldFetch = showQuotationReq && activeTab === "Select Service" && !hasServices && !isServiceLoading && !hasRequestedServices;
        if (!shouldFetch) return;

        setIsServiceLoading(true);
        setIsPartListLoading(true);
        setIsServiceBundleListLoading(true);
        setHasRequestedServices(true);

        // Fetch master service list
        fetchData(
            `${url}/master_service_list`,
            (error) => {
                setIsServiceLoading(false);
                toast.error(error?.response?.data?.message || "Failed to load services", {
                    autoClose: 3000,
                });
            },
            (response) => {
                setServiceBundles(response?.data || []);
                setIsServiceLoading(false);
            },
            false
        );

        // Fetch master part list
        fetchData(
            `${url}/master_part_list`,
            (error) => {
                setIsPartListLoading(false);
                toast.error(error?.response?.data?.message || "Failed to load parts", {
                    autoClose: 3000,
                });
            },
            (response) => {
                setPartList(response?.data || []);
                setIsPartListLoading(false);
            },
            false
        );

        // Fetch master service bundle list
        fetchData(
            `${url}/master_service_bundle_list`,
            (error) => {
                setIsServiceBundleListLoading(false);
                toast.error(error?.response?.data?.message || "Failed to load service bundles", {
                    autoClose: 3000,
                });
            },
            (response) => {
                setServiceBundleList(response?.data || []);
                setIsServiceBundleListLoading(false);
            },
            false
        );
    }, [showQuotationReq, activeTab, serviceBundles, isServiceLoading, hasRequestedServices]);

    useEffect(() => {
        fetchQuotations(page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    useEffect(() => {
        if (page !== 1) {
            setPage(1);
            return;
        }
        fetchQuotations(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, reloadTableToggle]);

    const handleVehicleSelect = (vehicleId) => {
        setSelectedVehicleId(vehicleId);
        setSelectedServiceIds([]);
        setSelectedPartIds([]);
        setSelectedBundleIds([]);
    };

    const handleServiceToggle = (serviceId) => {
        if (!serviceId) return;
        setSelectedServiceIds((prev) => {
            if (prev.includes(serviceId)) {
                return prev.filter((id) => id !== serviceId);
            }
            return [...prev, serviceId];
        });
    };

    const handlePartToggle = (partId) => {
        if (!partId) return;
        setSelectedPartIds((prev) => {
            if (prev.includes(partId)) {
                return prev.filter((id) => id !== partId);
            }
            return [...prev, partId];
        });
    };

    const handleBundleToggle = (bundleId) => {
        if (!bundleId) return;
        setSelectedBundleIds((prev) => {
            if (prev.includes(bundleId)) {
                // Remove bundle and clean up metadata
                setBundleMetadata((prevMeta) => {
                    const newMeta = { ...prevMeta };
                    delete newMeta[bundleId];
                    return newMeta;
                });
                return prev.filter((id) => id !== bundleId);
            }
            // Add bundle and initialize metadata for tire bundles
            // Check if bundle name matches tire mount and balance pattern
            const bundle = normalizedServiceBundleList.find((b) => b.id === bundleId);
            const isTireBundle = bundle && /tire.*mount.*and.*balance/i.test(bundle.title || "");
            if (isTireBundle) {
                setBundleMetadata((prevMeta) => ({
                    ...prevMeta,
                    [bundleId]: {
                        tireType: "",
                        tireQuantity: "",
                        tireNotes: "",
                    },
                }));
            }
            return [...prev, bundleId];
        });
    };

    const handleVehicleSearchChange = (value) => {
        setVehicleSearch(value);
    };

    const handleVehicleSearchReset = () => {
        setVehicleSearch("");
    };

    const handleAddCustomService = (name) => {
        const trimmed = name.trim();
        if (!trimmed) return;
        setCustomServiceList((prev) => [
            ...prev,
            {
                id: `custom-${Date.now()}-${prev.length}`,
                itemName: trimmed,
                price: null,
            },
        ]);
    };

    const handleRemoveCustomService = (idOrIndex) => {
        setCustomServiceList((prev) => {
            if (!prev.length) return prev;
            return prev.filter((item, index) => (item.id ?? index) !== idOrIndex);
        });
    };

    const handleBundleMetadataChange = (bundleId, field, value) => {
        setBundleMetadata((prev) => ({
            ...prev,
            [bundleId]: {
                ...prev[bundleId],
                [field]: value,
            },
        }));
    };

    const handleNotesChange = (value) => {
        setAdditionalNotes(value);
    };

    const handleCancelRequest = () => {
        setShowQuotationReq(false);
    };

    const handleSubmitQuote = () => {
        if (!selectedVehicle) {
            toast.error("Select a vehicle before submitting.", { autoClose: 3000 });
            return;
        }

        const selectedCount = selectedServices.length + selectedParts.length + selectedBundles.length + customServiceList.length;
        const hasTireQuantity = tireQuantity && Number(tireQuantity) > 0;
        if (selectedCount === 0 && !hasTireQuantity) {
            toast.error("Select at least one service, bundle, part, add a custom service, or specify tire quantity.", { autoClose: 3000 });
            return;
        }

        const fleetCustomerId = myInfo?.id || myInfo?.fleet_customer_id;
        if (!fleetCustomerId) {
            toast.error("Fleet customer information is missing.", { autoClose: 3000 });
            return;
        }

        const carId = selectedVehicle.raw?.car_id ?? selectedVehicle.raw?.id ?? selectedVehicle.id;
        if (!carId) {
            toast.error("Selected vehicle does not include an identifier.", { autoClose: 3000 });
            return;
        }

        // Service list with estimatedTime if available
        const serviceList = selectedServices.map((service) => {
            const item = {
                name: service.title,
                cost: Number(service.price) || 0,
            };
            if (service.estimatedTime > 0) {
                item.estimatedTime = service.estimatedTime;
            }
            return item;
        });

        // Material/Part list with estimatedTime if available
        const materialList = selectedParts.map((part) => {
            const item = {
                name: part.title,
                cost: Number(part.baseCost) || 0,
                labour: Number(part.labourCost) || 0,
            };
            if (part.estimatedTime > 0) {
                item.estimatedTime = part.estimatedTime;
            }
            return item;
        });

        // Service Bundle list with cost and tire metadata
        const serviceBundle = selectedBundles.map((bundle) => {
            const bundleData = {
                name: bundle.title,
                cost: Number(bundle.price) || 0,
            };
            // Add tire metadata for tire bundles
            const metadata = bundleMetadata[bundle.id];
            if (metadata) {
                if (metadata.tireBrand) bundleData.tireBrand = metadata.tireBrand.trim();
                if (metadata.tireSize) bundleData.tireSize = metadata.tireSize.trim();
                if (metadata.tireChangeReason) bundleData.tireChangeReason = metadata.tireChangeReason;
                if (metadata.tireType) bundleData.tireType = metadata.tireType;
                if (metadata.tireQuantity) bundleData.tireQuantity = Number(metadata.tireQuantity) || 0;
                if (metadata.tireNotes) bundleData.tireNotes = metadata.tireNotes.trim();
            }
            return bundleData;
        });

        // Custom list
        const customList = customServiceList.map((service) => ({
            name: service.itemName,
            cost: service.price || 0,
        }));

        // Calculate material cost (parts base cost total)
        const materialCost = selectedParts.reduce((sum, part) => sum + (Number(part.baseCost) || 0), 0);

        // Calculate technician cost (labour from parts + services hourly)
        const partsLabourCost = selectedParts.reduce((sum, part) => sum + (Number(part.labourCost) || 0), 0);
        const servicesLabourCost = selectedServices
            .filter(s => s.costType === "Hourly")
            .reduce((sum, service) => sum + (Number(service.price) || 0), 0);
        const technicianCost = partsLabourCost + servicesLabourCost;

        // Tire list
        const tireList = tireQuantity ? [{
            name: "Tire",
            quantity: Number(tireQuantity) || 0,
            cost: 0,
            notes: tireNotes.trim(),
        }] : [];

        const payload = {
            car_id: carId,
            fleet_customer_id: fleetCustomerId,
            serviceList,
            serviceBundle,
            materialList,
            customList,
            tireList,
            totalCost: overallTotal,
            tax: taxAmount,
            taxRatePercent: taxRate,
            technicianCost,
            materialCost,
            technicianHourlyCost: labourRate,
            paymentTerm: myInfo?.paymentTerms || null,
            serviceType: isMaintenance ? "maintenance" : "service",
        };

        const trimmedNotes = additionalNotes.trim();
        if (trimmedNotes) {
            payload.notes = trimmedNotes;
        }

        setIsSubmitting(true);
        postData(
            `${url}/service_req`,
            payload,
            (error) => {
                setIsSubmitting(false);
                toast.error(error?.response?.data?.message || "Failed to submit quotation request", {
                    autoClose: 3000,
                });
            },
            (data) => {
                setIsSubmitting(false);
                toast.success(data.message || "Quotation request submitted successfully", { autoClose: 3000 });
                setReloadTableToggle((prev) => !prev);
                setShowQuotationReq(false);
                setCustomServiceList([]);
                setTireQuantity("");
                setTireNotes("");
            },
            false
        );
    };

    function fetchQuotations(pageNumber = 1) {
        setIsQuotesLoading(true);
        const params = new URLSearchParams();
        params.append("page", pageNumber);
        if (statusFilter !== "all") {
            params.append("status", statusFilter);
        }

        fetchData(
            `${url}/service_req?${params.toString()}`,
            (error) => {
                setIsQuotesLoading(false);
                toast.error(error?.response?.data?.message || "Unable to load quotations", { autoClose: 3000 });
            },
            (response) => {
                setQuotes(response?.data || []);
                setIsNextPage(Boolean(response?.isNextPage));
                setIsQuotesLoading(false);
            },
            false
        );
    }

    function deriveVehicleCount(quote) {
        if (Array.isArray(quote?.vehicles)) return quote.vehicles.length;
        if (Array.isArray(quote?.fleet_customer_cars)) return quote.fleet_customer_cars.length;
        return quote?.fleet_customer_car ? 1 : quote?.car_id ? 1 : 0;
    }

    function deriveServiceCount(quote) {
        const serviceCount = Array.isArray(quote?.serviceList) ? quote.serviceList.length : 0;
        const bundleCount = Array.isArray(quote?.bundleList) ? quote.bundleList.length : 0;
        const materialCount = Array.isArray(quote?.materialList) ? quote.materialList.length : 0;
        const customCount = Array.isArray(quote?.customList) ? quote.customList.length : 0;
        return serviceCount + bundleCount + materialCount + customCount;
    }

    function extractVehicleDetails(quote) {
        const collections = [];
        if (Array.isArray(quote?.fleet_customer_cars)) collections.push(quote.fleet_customer_cars);
        if (Array.isArray(quote?.vehicles)) collections.push(quote.vehicles);
        if (quote?.fleet_customer_car) collections.push([quote.fleet_customer_car]);
        if (quote?.car) collections.push([quote.car]);
        const flattened = collections.flat();
        const seen = new Set();

        return flattened
            .map((vehicle) => {
                if (!vehicle) return null;
                const make = vehicle?.make || vehicle?.vehicle_make;
                const model = vehicle?.model || vehicle?.vehicle_model;
                const year = vehicle?.year || vehicle?.manufacture_year;
                const plate = vehicle?.plate || vehicle?.plate_number || vehicle?.registration_number;
                const vin = vehicle?.vin || vehicle?.vehicle_vin;
                const mileage = vehicle?.mileage || vehicle?.odometer || vehicle?.odo || vehicle?.kilometers;
                const identifier = vehicle?.id || vehicle?.car_id || vin || plate || `${make}-${model}-${year}`;
                return {
                    id: identifier,
                    label: [make, model, year].filter(Boolean).join(" ") || plate || vin || "Vehicle",
                    plate: plate || "-",
                    vin: vin || "-",
                    mileage,
                };
            })
            .filter((item) => {
                if (!item || !item.id) return Boolean(item);
                if (seen.has(item.id)) return false;
                seen.add(item.id);
                return true;
            });
    }

    function extractServiceList(quote) {
        const services = Array.isArray(quote?.serviceList) ? quote.serviceList : [];
        return services
            .map((service, index) => {
                if (!service) return null;
                const name = service?.name || service?.itemName || `Service ${index + 1}`;
                const cost = service?.cost ?? service?.price ?? 0;
                return {
                    id: service?.id || `service-${index}`,
                    name,
                    cost: Number(cost) || 0,
                    estimatedTime: service?.estimatedTime || 0,
                };
            })
            .filter(Boolean);
    }

    function extractBundleList(quote) {
        const bundles = Array.isArray(quote?.bundleList) ? quote.bundleList : [];
        return bundles
            .map((bundle, index) => {
                if (!bundle) return null;
                const name = bundle?.name || `Bundle ${index + 1}`;
                const cost = bundle?.cost ?? bundle?.price ?? 0;
                return {
                    id: bundle?.id || `bundle-${index}`,
                    name,
                    cost: Number(cost) || 0,
                    tireChangeReason: bundle?.tireChangeReason,
                    tireCost: bundle?.tireCost,
                    tireType: bundle?.tireType,
                    tireQuantity: bundle?.tireQuantity,
                    tireNotes: bundle?.tireNotes,
                    tireBrand: bundle?.tireBrand,
                    tireSize: bundle?.tireSize,
                };
            })
            .filter(Boolean);
    }

    function extractMaterialList(quote) {
        const materials = Array.isArray(quote?.materialList) ? quote.materialList : [];
        return materials
            .map((material, index) => {
                if (!material) return null;
                const name = material?.name || `Part ${index + 1}`;
                const cost = material?.cost ?? material?.price ?? 0;
                const labour = material?.labour ?? 0;
                return {
                    id: material?.id || `material-${index}`,
                    name,
                    cost: Number(cost) || 0,
                    labour: Number(labour) || 0,
                    estimatedTime: material?.estimatedTime || 0,
                };
            })
            .filter(Boolean);
    }

    function extractCustomServiceList(quote) {
        const customServices = Array.isArray(quote?.customList) ? quote.customList : [];
        return customServices
            .map((service, index) => {
                if (!service) return null;
                const name = service?.name || service?.itemName || `Custom Service ${index + 1}`;
                const cost = service?.cost ?? service?.price ?? 0;
                return {
                    id: service?.id || `custom-${index}`,
                    name,
                    cost: Number(cost) || 0,
                };
            })
            .filter(Boolean);
    }

    function extractTireList(quote) {
        const tires = Array.isArray(quote?.tireList) ? quote.tireList : [];
        return tires
            .map((tire, index) => {
                if (!tire) return null;
                return {
                    id: tire?.id || `tire-${index}`,
                    name: tire?.name || "Tire",
                    quantity: Number(tire?.quantity) || 0,
                    price: Number(tire?.cost) || 0,
                    notes: tire?.notes || "",
                };
            })
            .filter(Boolean);
    }

    function handleStatusFilterChange(nextStatus) {
        if (nextStatus === statusFilter) return;
        setStatusFilter(nextStatus);
    }

    function toggleQuoteExpansion(quoteKey) {
        setExpandedQuoteId((prev) => (prev === quoteKey ? null : quoteKey));
    }

    function handleApproveQuote(quoteId, quote) {
        if (!quoteId || approvingQuoteId) return;
        setApprovingQuoteId(quoteId);
        patchData(
            `${url}/approve_service_req/${quoteId}`,
            { statusTracking: quote.statusTracking || null },
            (error) => {
                setApprovingQuoteId(null);
                toast.error(error?.response?.data?.message || "Failed to approve quotation", { autoClose: 3000 });
            },
            (response) => {
                setApprovingQuoteId(null);
                toast.success(response?.message || "Quotation approved successfully", { autoClose: 3000 });
                setReloadTableToggle((prev) => !prev);
            },
            false
        );
    }

    function handleRejectQuote(quoteId, quote) {
        if (!quoteId || rejectingQuoteId) return;
        setRejectingQuoteId(quoteId);
        patchData(
            `${url}/reject_service_req/${quoteId}`,
            { statusTracking: quote.statusTracking || null },
            (error) => {
                setRejectingQuoteId(null);
                toast.error(error?.response?.data?.message || "Failed to reject quotation", { autoClose: 3000 });
            },
            (response) => {
                setRejectingQuoteId(null);
                toast.success(response?.message || "Quotation rejected successfully", { autoClose: 3000 });
                setReloadTableToggle((prev) => !prev);
            },
            false
        );
    }

    function handleCompletePayment(quoteId) {
        if (!quoteId || paymentLoadingQuoteId) return;
        setPaymentLoadingQuoteId(quoteId);
        fetchData(
            `${url}/service_req_payment/${quoteId}`,
            (error) => {
                setPaymentLoadingQuoteId(null);
                toast.error(error?.response?.data?.message || "Failed to get payment link", { autoClose: 3000 });
            },
            (response) => {
                setPaymentLoadingQuoteId(null);
                if (response?.paymentLink) {
                    window.location.href = response.paymentLink;
                } else {
                    toast.error("Payment link not available", { autoClose: 3000 });
                }
            },
            false
        );
    }

    // Refresh quotations when user returns to the page (e.g., after completing payment)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                setReloadTableToggle((prev) => !prev);
            }
        };

        const handleFocus = () => {
            setReloadTableToggle((prev) => !prev);
        };

        const handlePageShow = (event) => {
            if (event.persisted) {
                setReloadTableToggle((prev) => !prev);
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("focus", handleFocus);
        window.addEventListener("pageshow", handlePageShow);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("focus", handleFocus);
            window.removeEventListener("pageshow", handlePageShow);
        };
    }, []);

    function goToPreviousPage() {
        if (!canGoBack) return;
        setPage((prev) => Math.max(prev - 1, 1));
    }

    function goToNextPage() {
        if (!canGoForward) return;
        setPage((prev) => prev + 1);
    }

    return (
        <div className='p-3 sm:p-4' style={{ backgroundColor: "#e6f9ec", borderRadius: "12px" }}>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mb-4">
                <div className={title}>Service Requests</div>
                <button
                    onClick={() => setShowQuotationReq(prev => !prev)}
                    className="inline-flex items-center justify-center rounded-full bg-primary hover:bg-primary-dark text-white px-6 sm:px-8 py-3 sm:py-3.5 text-sm sm:text-base font-semibold shadow-base transition-colors whitespace-nowrap"
                >
                    {showQuotationReq ? "Cancel" : "Request Service"}
                </button>
            </div>

            {showQuotationReq && (
                <QuotationRequestPanel
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    vehicles={normalizedVehicles}
                    isLoading={isVehiclesLoading}
                    serviceBundles={normalizedServices}
                    isServiceLoading={isServiceLoading}
                    partList={normalizedPartList}
                    isPartListLoading={isPartListLoading}
                    serviceBundleList={normalizedServiceBundleList}
                    isServiceBundleListLoading={isServiceBundleListLoading}
                    selectedVehicleId={selectedVehicleId}
                    onVehicleSelect={handleVehicleSelect}
                    selectedServiceIds={selectedServiceIds}
                    onServiceToggle={handleServiceToggle}
                    selectedPartIds={selectedPartIds}
                    onPartToggle={handlePartToggle}
                    selectedBundleIds={selectedBundleIds}
                    onBundleToggle={handleBundleToggle}
                    bundleMetadata={bundleMetadata}
                    onBundleMetadataChange={handleBundleMetadataChange}
                    selectedVehicle={selectedVehicle}
                    selectedServices={selectedServices}
                    selectedParts={selectedParts}
                    selectedBundles={selectedBundles}
                    subtotal={totalPrice}
                    overallTotal={overallTotal}
                    taxRate={taxRate}
                    taxAmount={taxAmount}
                    partsBaseCostTotal={partsBaseCostTotal}
                    notes={additionalNotes}
                    onNotesChange={handleNotesChange}
                    onSubmit={handleSubmitQuote}
                    onCancel={handleCancelRequest}
                    isSubmitting={isSubmitting}
                    customServices={customServiceList}
                    onAddCustomService={handleAddCustomService}
                    onRemoveCustomService={handleRemoveCustomService}
                    vehicleSearch={vehicleSearch}
                    onVehicleSearchChange={handleVehicleSearchChange}
                    onVehicleSearchReset={handleVehicleSearchReset}
                    tireQuantity={tireQuantity}
                    onTireQuantityChange={setTireQuantity}
                    tireNotes={tireNotes}
                    onTireNotesChange={setTireNotes}
                    isMaintenance={isMaintenance}
                    onIsMaintenanceChange={setIsMaintenance}
                />
            )}

            <div className="mt-4 sm:mt-6 flex flex-col gap-4">
                <div className="flex flex-col gap-3 rounded-2xl border border-emerald-100 bg-white p-3 sm:p-4 shadow-[0_12px_28px_rgba(22,101,52,0.12)]">
                    <div className="text-sm font-semibold text-emerald-800">Filter by status</div>
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 rounded-2xl sm:rounded-full border border-emerald-100 bg-emerald-50/40 p-1">
                        {[
                            { label: "All", value: "all", activeClass: "bg-emerald-600 text-white shadow-[0_8px_20px_rgba(16,185,129,0.28)]" },
                            { label: "Pending", value: "pending", activeClass: "bg-amber-500 text-white shadow-[0_8px_20px_rgba(245,158,11,0.28)]" },
                            ...(myInfo?.bypassApproval === "no" ? [
                                { label: "Modified", value: "modified", activeClass: "bg-blue-500 text-white shadow-[0_8px_20px_rgba(59,130,246,0.28)]" },
                                { label: "Approved", value: "approved", activeClass: "bg-emerald-600 text-white shadow-[0_8px_20px_rgba(16,185,129,0.28)]" },
                                { label: "Rejected", value: "rejected", activeClass: "bg-rose-500 text-white shadow-[0_8px_20px_rgba(244,63,94,0.28)]" },
                            ] : []),
                            { label: "InService", value: "inservice", activeClass: "bg-cyan-500 text-white shadow-[0_8px_20px_rgba(6,182,212,0.28)]" },
                            { label: "Completed", value: "completed", activeClass: "bg-green-600 text-white shadow-[0_8px_20px_rgba(22,163,74,0.28)]" },
                            // { label: "Failed", value: "failed", activeClass: "bg-red-500 text-white shadow-[0_8px_20px_rgba(239,68,68,0.28)]" },
                            { label: "Paid", value: "paid", activeClass: "bg-purple-500 text-white shadow-[0_8px_20px_rgba(168,85,247,0.28)]" },
                        ].map((option) => {
                            const isActive = statusFilter === option.value;
                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => handleStatusFilterChange(option.value)}
                                    className={`rounded-full px-3 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold transition-colors ${isActive
                                        ? option.activeClass
                                        : "text-emerald-700 hover:bg-white"
                                        }`}
                                >
                                    {option.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {isQuotesLoading && (
                    <div className="rounded-2xl border border-emerald-100 bg-white px-6 py-12 text-center text-emerald-600 shadow-[0_18px_40px_rgba(22,101,52,0.12)]">
                        Loading Service Requests...
                    </div>
                )}

                {!isQuotesLoading && !hasQuotes && (
                    <div className="rounded-2xl border border-dashed border-emerald-200 bg-white px-6 py-16 text-center text-emerald-500 shadow-[0_12px_30px_rgba(22,101,52,0.12)]">
                        No record found.
                    </div>
                )}

                {!isQuotesLoading && hasQuotes && (
                    <div className="grid grid-cols-1 gap-4">
                        {quotes.map((quote) => {
                            const quoteId = quote?.id || "Quotation";
                            const createdOn = quote?.createdAt
                            const notes = quote?.notes || "";
                            const vehicleCount = deriveVehicleCount(quote);
                            const serviceCount = deriveServiceCount(quote);
                            const isExpanded = expandedQuoteId === quoteId;
                            const vehicleDetails = extractVehicleDetails(quote);
                            const serviceDetails = extractServiceList(quote);
                            const bundleDetails = extractBundleList(quote);
                            const materialDetails = extractMaterialList(quote);
                            const customServiceDetails = extractCustomServiceList(quote);
                            const tireDetails = extractTireList(quote);
                            const hasServiceDetails = serviceDetails.length > 0;
                            const hasBundleDetails = bundleDetails.length > 0;
                            const hasMaterialDetails = materialDetails.length > 0;
                            const hasCustomServices = customServiceDetails.length > 0;
                            const hasTireDetails = tireDetails.length > 0;

                            // Tax and cost breakdown
                            const quoteTax = quote?.tax || 0;
                            const quoteTaxRate = quote?.taxRatePercent || 0;
                            const quoteTechnicianCost = quote?.technicianCost || 0;
                            const quoteMaterialCost = quote?.materialCost || 0;
                            const quoteSubtotal = quoteTechnicianCost + quoteMaterialCost;

                            return (
                                <div
                                    key={quoteId}
                                    className={`rounded-2xl sm:rounded-3xl border border-emerald-100 bg-white px-3 sm:px-5 py-3 sm:py-4 shadow-[0_18px_40px_rgba(22,101,52,0.12)] transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-[0_22px_48px_rgba(22,101,52,0.18)] ${isExpanded ? "ring-2 ring-emerald-300" : ""}`}
                                >
                                    <button
                                        type="button"
                                        onClick={() => toggleQuoteExpansion(quoteId)}
                                        className="flex w-full flex-col gap-3 text-left"
                                        aria-expanded={isExpanded}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                                <div className="flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 flex-shrink-0">
                                                    <FiClock size={14} className="sm:w-4 sm:h-4" />
                                                </div>
                                                <span className="text-sm sm:text-md font-semibold text-emerald-900 break-words">QU000_{quoteId}</span>
                                            </div>
                                            <div className="flex items-center justify-center flex-shrink-0">
                                                <FiChevronDown className={`text-emerald-600 transition-transform ${isExpanded ? "rotate-180" : ""}`} size={20} />
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between gap-2 flex-wrap">
                                            <div className="flex items-center gap-1.5 text-xs text-emerald-600">
                                                <FiCalendar size={12} className="flex-shrink-0" />
                                                <span className="text-[10px] sm:text-xs">{formatDateTime(createdOn)}</span>
                                            </div>
                                            <span className={`rounded-full px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-semibold whitespace-nowrap ${getStatusBadgeClass(quote?.status)}`}>
                                                {formatStatus(quote?.status)}
                                            </span>
                                        </div>
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 text-sm text-emerald-600">
                                            <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs font-semibold uppercase tracking-wide">
                                                <span>{vehicleCount} vehicle(s)</span>
                                                <span className="h-1 w-1 rounded-full bg-emerald-300" />
                                                <span>{serviceCount} service(s)</span>
                                            </div>
                                            <div className="flex items-center gap-1.5 sm:gap-2">
                                                <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-emerald-500">Total Amount:</span>
                                                <span className="text-base sm:text-lg font-bold text-slate-900">{formatCurrency(quote?.totalCost)}</span>
                                            </div>
                                        </div>
                                    </button>

                                    {isExpanded && (
                                        <div className="mt-4 space-y-4 border-t border-emerald-100 pt-4 text-sm text-slate-700">
                                            {/* Status Tracking Flow */}
                                            {Array.isArray(quote?.statusTracking) && quote.statusTracking.length > 0 && (
                                                <StatusTrackingFlow statusTracking={quote.statusTracking} />
                                            )}

                                            {vehicleDetails.length > 0 && (
                                                <div>
                                                    <div className="mb-2 text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-emerald-500">Vehicles</div>
                                                    <ul className="space-y-2">
                                                        {vehicleDetails.map((vehicle) => (
                                                            <li
                                                                key={vehicle.id}
                                                                className="rounded-lg sm:rounded-xl bg-emerald-50/70 px-2.5 sm:px-3 py-2 text-xs text-emerald-800"
                                                            >
                                                                <div className="font-semibold text-emerald-900 break-words">{vehicle.label}</div>
                                                                <div className="flex flex-wrap gap-2 sm:gap-3 text-[11px] sm:text-xs">
                                                                    <span className="break-all">VIN: {vehicle.vin}</span>
                                                                </div>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            {hasServiceDetails && (
                                                <div>
                                                    <div className="mb-2 text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-emerald-500">Services</div>
                                                    <ul className="space-y-2">
                                                        {serviceDetails.map((service) => (
                                                            <li
                                                                key={service.id}
                                                                className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2 rounded-lg sm:rounded-xl border border-emerald-100 bg-emerald-50/50 px-2.5 sm:px-3 py-2 text-xs"
                                                            >
                                                                <div className="break-words">
                                                                    <span className="font-semibold text-emerald-800">{service.name}</span>
                                                                    {service.estimatedTime > 0 && (
                                                                        <span className="ml-1 sm:ml-2 text-emerald-600 text-[10px] sm:text-xs">({service.estimatedTime} min)</span>
                                                                    )}
                                                                </div>
                                                                <span className="text-emerald-600 font-medium whitespace-nowrap">{formatCurrency(service.cost)}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            {hasBundleDetails && (
                                                <div>
                                                    <div className="mb-2 text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-blue-500">Service Bundles</div>
                                                    <ul className="space-y-2">
                                                        {bundleDetails.map((bundle) => (
                                                            <li
                                                                key={bundle.id}
                                                                className="rounded-lg sm:rounded-xl border border-blue-100 bg-blue-50/50 px-2.5 sm:px-3 py-2 text-xs"
                                                            >
                                                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2">
                                                                    <span className="font-semibold text-blue-800 break-words">{bundle.name}</span>
                                                                    <span className="text-blue-600 font-medium whitespace-nowrap">{formatCurrency(bundle.cost)}</span>
                                                                </div>
                                                                {/* Tire Details - shown if bundle has any tire information */}
                                                                <div className="mt-2 pt-2 border-t border-blue-100">
                                                                    <div className="flex items-center gap-1 mb-1">
                                                                        <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-400">Tire Details</span>
                                                                    </div>
                                                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                                                                        {bundle?.tireBrand && (
                                                                            <div className="flex justify-between">
                                                                                <span className="text-slate-500">Brand:</span>
                                                                                <span className="font-medium text-slate-700">{bundle.tireBrand}</span>
                                                                            </div>
                                                                        )}
                                                                        {bundle?.tireSize && (
                                                                            <div className="flex justify-between">
                                                                                <span className="text-slate-500">Size:</span>
                                                                                <span className="font-medium text-slate-700">{bundle.tireSize}</span>
                                                                            </div>
                                                                        )}
                                                                        {bundle?.tireType && (
                                                                            <div className="flex justify-between">
                                                                                <span className="text-slate-500">Type:</span>
                                                                                <span className="font-medium text-slate-700">{bundle.tireType}</span>
                                                                            </div>
                                                                        )}
                                                                        {bundle?.tireChangeReason && (
                                                                            <div className="flex justify-between">
                                                                                <span className="text-slate-500">Change Reason:</span>
                                                                                <span className="font-medium text-slate-700">{(bundle.tireChangeReason)}</span>
                                                                            </div>
                                                                        )}
                                                                        {bundle?.tireQuantity !== undefined && (
                                                                            <div className="flex justify-between">
                                                                                <span className="text-slate-500">Quantity:</span>
                                                                                <span className="font-medium text-slate-700">{bundle.tireQuantity}</span>
                                                                            </div>
                                                                        )}
                                                                        {(bundle?.tireCost !== undefined && bundle?.tireCost !== null) && (
                                                                            <div className="flex justify-between">
                                                                                <span className="text-slate-500">Price Per Tire:</span>
                                                                                <span className="font-medium text-blue-600">{formatCurrency(bundle.tireCost)}</span>
                                                                            </div>
                                                                        )}
                                                                        {bundle?.tireNotes && (
                                                                            <div className="col-span-2 flex gap-1">
                                                                                <span className="text-slate-500">Notes:</span>
                                                                                <span className="font-medium text-slate-700">{bundle.tireNotes}</span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            {hasMaterialDetails && (
                                                <div>
                                                    <div className="mb-2 text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-amber-500">Parts / Materials</div>
                                                    <ul className="space-y-2">
                                                        {materialDetails.map((material) => (
                                                            <li
                                                                key={material.id}
                                                                className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2 rounded-lg sm:rounded-xl border border-amber-100 bg-amber-50/50 px-2.5 sm:px-3 py-2 text-xs"
                                                            >
                                                                <div className="break-words">
                                                                    <span className="font-semibold text-amber-800">{material.name}</span>
                                                                    {material.estimatedTime > 0 && (
                                                                        <span className="ml-1 sm:ml-2 text-amber-600 text-[10px] sm:text-xs">({material.estimatedTime} min)</span>
                                                                    )}
                                                                </div>
                                                                <span className="text-amber-600 font-medium whitespace-nowrap">{formatCurrency(material.cost + material.labour)}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            {hasCustomServices && (
                                                <div>
                                                    <div className="mb-2 text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-purple-500">Custom Services</div>
                                                    <ul className="space-y-2">
                                                        {customServiceDetails.map((service) => (
                                                            <li
                                                                key={service.id}
                                                                className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2 rounded-lg sm:rounded-xl border border-dashed border-purple-200 bg-purple-50/50 px-2.5 sm:px-3 py-2 text-xs"
                                                            >
                                                                <span className="font-semibold text-purple-800 break-words">{service.name}</span>
                                                                <span className="text-purple-600 font-medium whitespace-nowrap">{formatCurrency(service.cost)}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            {hasTireDetails && (
                                                <div>
                                                    <div className="mb-2 text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-violet-500">Tires</div>
                                                    <ul className="space-y-2">
                                                        {tireDetails.map((tire) => (
                                                            <li
                                                                key={tire.id}
                                                                className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2 rounded-lg sm:rounded-xl border border-violet-100 bg-violet-50/50 px-2.5 sm:px-3 py-2 text-xs"
                                                            >
                                                                <div className="break-words">
                                                                    <span className="font-semibold text-violet-800">{tire.name} x {tire.quantity}</span>
                                                                    {tire.notes && (
                                                                        <span className="ml-1 sm:ml-2 text-violet-600 text-[10px] sm:text-xs">({tire.notes})</span>
                                                                    )}
                                                                </div>
                                                                <span className="text-violet-600 font-medium">{formatCurrency(tire.price * tire.quantity)}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            {/* Cost Breakdown */}
                                            {(quoteSubtotal > 0 || quoteTax > 0 || quote?.totalCost > 0 || hasServiceDetails || hasBundleDetails || hasMaterialDetails || hasCustomServices || hasTireDetails) && (
                                                <div>
                                                    <div className="mb-2 text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-slate-500">Cost Breakdown</div>
                                                    <div className="rounded-lg sm:rounded-xl border border-slate-200 bg-slate-50/50 px-2.5 sm:px-3 py-2 text-xs space-y-1">
                                                        {quoteTechnicianCost > 0 && (
                                                            <div className="flex justify-between text-slate-600">
                                                                <span>Labour Cost:</span>
                                                                <span className="font-medium">{formatCurrency(quoteTechnicianCost)}</span>
                                                            </div>
                                                        )}
                                                        {quoteMaterialCost > 0 && (
                                                            <div className="flex justify-between text-slate-600">
                                                                <span>Material Cost:</span>
                                                                <span className="font-medium">{formatCurrency(quoteMaterialCost)}</span>
                                                            </div>
                                                        )}
                                                        {quoteSubtotal > 0 && (
                                                            <div className="flex justify-between text-slate-700 border-t border-slate-200 pt-1 mt-1">
                                                                <span>Subtotal:</span>
                                                                <span className="font-semibold">{formatCurrency(quoteSubtotal)}</span>
                                                            </div>
                                                        )}
                                                        {quoteTax > 0 && (
                                                            <div className="flex justify-between text-slate-600">
                                                                <span>Tax ({quoteTaxRate}%):</span>
                                                                <span className="font-medium">{formatCurrency(quoteTax)}</span>
                                                            </div>
                                                        )}
                                                        <div className={`flex justify-between text-emerald-700 ${(quoteTechnicianCost > 0 || quoteMaterialCost > 0 || quoteSubtotal > 0 || quoteTax > 0) ? 'border-t border-slate-200 pt-1 mt-1' : ''}`}>
                                                            <span className="font-semibold">Total:</span>
                                                            <span className="font-bold">{formatCurrency(quote?.totalCost)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {notes && (
                                                <div>
                                                    <div className="mb-2 text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-emerald-500">Notes</div>
                                                    <p className="rounded-lg sm:rounded-xl bg-emerald-50/60 px-2.5 sm:px-3 py-2 text-xs text-emerald-700 break-words">{notes}</p>
                                                </div>
                                            )}

                                            {quote?.estimatedDate && (
                                                <div>
                                                    <div className="mb-2 text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-blue-500">Estimated Completion Date</div>
                                                    <p className="rounded-lg sm:rounded-xl bg-blue-50/60 px-2.5 sm:px-3 py-2 text-xs text-blue-700 font-medium">
                                                        {formatDate(quote.estimatedDate)}
                                                    </p>
                                                </div>
                                            )}

                                            {String(quote?.status || "").toLowerCase() === "modified" && (
                                                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRejectQuote(quoteId, quote)}
                                                        disabled={rejectingQuoteId === quoteId || approvingQuoteId === quoteId}
                                                        className={`rounded-full px-4 sm:px-5 py-2 text-xs sm:text-sm font-semibold transition-colors ${rejectingQuoteId === quoteId
                                                            ? "bg-rose-300 text-white cursor-not-allowed"
                                                            : "border border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                                                            }`}
                                                    >
                                                        {rejectingQuoteId === quoteId ? "Rejecting..." : "Reject"}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleApproveQuote(quoteId, quote)}
                                                        disabled={approvingQuoteId === quoteId || rejectingQuoteId === quoteId}
                                                        className={`rounded-full px-4 sm:px-5 py-2 text-xs sm:text-sm font-semibold transition-colors ${approvingQuoteId === quoteId
                                                            ? "bg-emerald-300 text-white cursor-not-allowed"
                                                            : "bg-emerald-600 text-white hover:bg-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,0.25)]"
                                                            }`}
                                                    >
                                                        {approvingQuoteId === quoteId ? "Approving..." : "Approve"}
                                                    </button>
                                                </div>
                                            )}

                                            {(String(quote?.status || "").toLowerCase() === "completed" || String(quote?.status || "").toLowerCase() === "failed") && (
                                                <div className="flex justify-end pt-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleCompletePayment(quoteId)}
                                                        disabled={paymentLoadingQuoteId === quoteId}
                                                        className={`rounded-full px-4 sm:px-5 py-2 text-xs sm:text-sm font-semibold transition-colors w-full sm:w-auto ${paymentLoadingQuoteId === quoteId
                                                            ? "bg-blue-300 text-white cursor-not-allowed"
                                                            : "bg-blue-600 text-white hover:bg-blue-700 shadow-[0_8px_20px_rgba(59,130,246,0.25)]"
                                                            }`}
                                                    >
                                                        {paymentLoadingQuoteId === quoteId ? "Loading..." : "Complete Payment"}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {!isQuotesLoading && hasQuotes && (
                    <div className="flex flex-col gap-3 rounded-2xl border border-emerald-100 bg-white px-3 sm:px-5 py-3 sm:py-4 shadow-[0_12px_30px_rgba(22,101,52,0.12)] sm:flex-row sm:items-center sm:justify-between">
                        <div className="text-xs sm:text-sm font-medium text-emerald-600 text-center sm:text-left">Page {page}</div>
                        <div className="flex gap-2 sm:gap-3">
                            <button
                                type="button"
                                onClick={goToPreviousPage}
                                disabled={!canGoBack}
                                className={`flex-1 sm:flex-none px-4 sm:px-5 py-2 rounded-full border text-xs sm:text-sm font-semibold transition-colors ${canGoBack
                                    ? "border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50"
                                    : "border-emerald-100 bg-emerald-50 text-emerald-300 cursor-not-allowed"
                                    }`}
                            >
                                Previous
                            </button>
                            <button
                                type="button"
                                onClick={goToNextPage}
                                disabled={!canGoForward}
                                className={`flex-1 sm:flex-none px-4 sm:px-5 py-2 rounded-full border text-xs sm:text-sm font-semibold transition-colors ${canGoForward
                                    ? "border-emerald-500 bg-emerald-600 text-white shadow-[0_10px_25px_rgba(16,185,129,0.25)] hover:bg-emerald-700"
                                    : "border-emerald-100 bg-emerald-50 text-emerald-300 cursor-not-allowed"
                                    }`}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Quotations;
